import { Component, Input, forwardRef, HostListener, ElementRef, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-multiselect-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './multiselect-dropdown.component.html',
  styleUrls: ['./multiselect-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectDropdownComponent),
      multi: true
    }
  ]
})
export class MultiselectDropdownComponent implements ControlValueAccessor, OnChanges {
  // ngOnInit() {
  //   throw new Error('Method not implemented.');
  // }
  @Input() list: any[] = [];
  @Input() placeholder: string = 'Select';
  @Input() displayKey: string = 'name';
  @Input() valueKey: string = 'id';

  showDropDown: boolean = false;
  selectedItems: any[] = [];
  disabled = false;
  searchTerm: string = '';

  // ControlValueAccessor callbacks
  onChange: any = () => {};
  onTouched: any = () => {};
  selectionChange: any;
  initialSelectedValues: number[] | undefined;

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) { }

  // cache latest primitive value array written via writeValue so we can compute
  private cachedValue: any[] = [];

  ngOnChanges(changes: SimpleChanges) {
    const options = this.list || [];
    // When the options list changes after control value was already written,
    // recalculate selectedItems so chips are rendered correctly.
    if (changes['list'] && this.cachedValue?.length) {
      this.selectedItems = options.filter(item => this.cachedValue.includes(item[this.valueKey]));
      this.cdr.detectChanges();
    }
  }

  writeValue(value: any[]): void {
    const options = this.list || [];
    this.cachedValue = Array.isArray(value) ? value : [];
    if (this.cachedValue.length) {
      this.selectedItems = options.filter(item => this.cachedValue.includes(item[this.valueKey]));
    } else {
      this.selectedItems = [];
    }
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges(); // Ensure view updates for disabled state
  }

  toggleDropDown() {
    if (!this.disabled) {
      this.showDropDown = !this.showDropDown;
      if (!this.showDropDown) {
        this.onTouched();
        this.searchTerm = '';
      }
      this.cdr.detectChanges(); // Ensure view updates after toggle
    }
  }

  onCheckboxChange(item: any, event: any) {
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.selectedItems.find(i => i[this.valueKey] === item[this.valueKey])) {
        this.selectedItems.push(item);
      }
    } else {
      this.selectedItems = this.selectedItems.filter(i => i[this.valueKey] !== item[this.valueKey]);
    }
    const selectedValues = this.selectedItems.map(i => i[this.valueKey]);
    this.onChange(selectedValues);
    this.cdr.detectChanges(); // Ensure view updates after selection change
  }

  isAllSelected() {
    const options = this.list || [];
    return options.length > 0 && this.selectedItems.length === options.length;
  }

  isIndeterminate() {
    return this.selectedItems.length > 0 && !this.isAllSelected();
  }

  toggleAllSelection(event: any) {
    const isChecked = event.target.checked;
    const options = this.list || [];
    this.selectedItems = isChecked ? [...options] : [];
    const selectedValues = this.selectedItems.map(i => i[this.valueKey]);
    this.onChange(selectedValues);
    this.cdr.detectChanges(); // Ensure view updates after select all
  }

  isSelected(item: any): boolean {
    return this.selectedItems.some(i => i[this.valueKey] === item[this.valueKey]);
  }

  getDisplayText() {
    if (this.selectedItems.length === 0) {
      return this.placeholder;
    }
    if (this.selectedItems.length === 1) {
      return this.selectedItems[0][this.displayKey];
    }
    return `${this.selectedItems.length} selected`;
  }

  onSearchTermChange(value: string) {
    console.log('Search Term changed to:', value); // Debug log
    this.searchTerm = value; // Ensure searchTerm is updated
    this.cdr.detectChanges(); // Explicitly trigger change detection
  }

  get filteredList(): any[] {
  const options = this.list || [];
  if (!this.searchTerm) {
    return options;
  }

  const normalizedSearch = this.searchTerm
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ''); // 🔥 remove all spaces

  return options.filter(item => {
    const normalizedItem = item[this.displayKey]
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '');

    return normalizedItem.includes(normalizedSearch);
  });
}


  @HostListener('window:click', ['$event'])
  onClick(event: Event) {
    setTimeout(() => {
      if (this.showDropDown && !this.elementRef.nativeElement.contains(event.target)) {
        console.log('Click was outside the component AND dropdown is open. Closing dropdown.'); // Debug log
        this.showDropDown = false;
        this.onTouched();
        this.searchTerm = '';
        this.cdr.detectChanges();
      }
    }, 0);
  }

  showFocus() {
    this.showDropDown = true;
    this.cdr.detectChanges();
  }

  removeSelectedItem(item: any, event: Event) {
    event.stopPropagation();
    this.selectedItems = this.selectedItems.filter(i => i[this.valueKey] !== item[this.valueKey]);
    const selectedValues = this.selectedItems.map(i => i[this.valueKey]);
    this.onChange(selectedValues);
    this.cdr.detectChanges();
  }
}
